import * as XLSX from 'xlsx';
import {
  OVERRIDE_TYPE_OPTIONS,
  complianceTypeLabel,
  getIssueComplianceType,
  pathTypeLabel,
  statusLabel,
  verdictLabel,
} from './complianceIssuePresenters';

/** Columns aligned with ComplianceIssuesTable + useful export fields. */
export const EXPORT_TABLE_COLUMNS = [
  { key: 'ncCode', label: 'ID', description: 'Issue display ID (e.g. COD-015)' },
  { key: 'topic', label: 'Topic', description: 'Short issue title' },
  { key: 'requirementArticle', label: 'Requirement', description: 'Requirement article / section ref' },
  { key: 'evidenceDoc', label: 'Evidence Doc', description: 'Deliverable document name' },
  { key: 'evidenceLocation', label: 'Evidence Page', description: 'Page or location in evidence doc' },
  { key: 'status', label: 'Status', description: 'Workflow status' },
  { key: 'complianceType', label: 'Compliance Type', description: 'Building code, client req., standards' },
  { key: 'assignee', label: 'Assigned To', description: 'Assigned team member' },
  { key: 'created', label: 'Created', description: 'Issue created date' },
  { key: 'verdict', label: 'Verdict', description: 'Compliant / non-compliant / needs review' },
  { key: 'researchFinding', label: 'Research Finding', description: 'Identified non-compliance narrative' },
];

export const DEFAULT_TABLE_COLUMN_SELECTION = Object.fromEntries(
  EXPORT_TABLE_COLUMNS.map((c) => [c.key, c.key !== 'researchFinding'])
);

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function overrideTypeLabel(value) {
  const opt = OVERRIDE_TYPE_OPTIONS.find((o) => o.value === value);
  return opt?.label || value || '';
}

function formatRefs(refs) {
  return (refs || [])
    .map((r) => r?.label || r?.value || '')
    .filter(Boolean)
    .join('; ');
}

function formatSourceBlock(source) {
  if (!source) return '';
  const parts = [];
  if (source.docName) parts.push(source.docName);
  if (source.location) parts.push(`Location: ${source.location}`);
  if (source.statement) parts.push(source.statement);
  return parts.join(' — ');
}

function getTableCellValue(issue, col, getAssigneeName) {
  switch (col) {
    case 'ncCode':
      return issue.ncCode || issue.display_id || '';
    case 'topic':
      return issue.topic || issue.identifiedIssue || '';
    case 'requirementArticle':
      return issue.requirementArticle || formatRefs(issue.requirementRefs) || '';
    case 'evidenceDoc':
      return issue.evidenceDoc || '';
    case 'evidenceLocation':
      return issue.evidenceLocation || issue.evidenceDocMeta?.pageNumber || '';
    case 'status':
      return statusLabel(issue.status);
    case 'complianceType':
      return complianceTypeLabel(getIssueComplianceType(issue));
    case 'assignee':
      return getAssigneeName ? getAssigneeName(issue.assignedTo) : issue.assignedTo || '';
    case 'created':
      return issue.formattedCreatedAt || issue.createdAt || '';
    case 'verdict':
      return verdictLabel(issue.verdict);
    case 'researchFinding':
      return issue.researchFinding || issue.identifiedIssue || issue.description || '';
    default:
      return issue[col] || '';
  }
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function buildTableRows(data, columns, columnDefs, getAssigneeName) {
  const labelByKey = Object.fromEntries(columnDefs.map((c) => [c.key, c.label]));
  const headers = columns.map((col) => labelByKey[col] || col);
  const rows = data.map((issue) =>
    columns.map((col) => getTableCellValue(issue, col, getAssigneeName))
  );
  return { headers, rows, aoa: [headers, ...rows] };
}

function exportTableToCSV(data, columns, filename, columnDefs, getAssigneeName) {
  const { aoa } = buildTableRows(data, columns, columnDefs, getAssigneeName);
  const csv = aoa
    .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const name = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), name);
}

function exportTableToXlsx(data, columns, filename, columnDefs, getAssigneeName) {
  const { headers, rows, aoa } = buildTableRows(data, columns, columnDefs, getAssigneeName);
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet['!cols'] = headers.map((header, colIdx) => {
    const maxLen = Math.max(
      header.length,
      ...rows.map((row) => String(row[colIdx] ?? '').length)
    );
    return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Compliance Issues');
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const name = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  downloadBlob(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    name
  );
}

function buildTableHTML(data, columns, timestamp, projectName, columnDefs, getAssigneeName) {
  const labelByKey = Object.fromEntries(columnDefs.map((c) => [c.key, c.label]));
  const headers = columns.map((col) => labelByKey[col] || col);
  const rows = data
    .map(
      (issue) => `
      <tr>
        ${columns
          .map((col) => `<td>${escapeHtml(getTableCellValue(issue, col, getAssigneeName))}</td>`)
          .join('')}
      </tr>`
    )
    .join('');

  return wrapExportDocument({
    title: 'Compliance Table Export',
    subtitle: `${projectName ? `${escapeHtml(projectName)} · ` : ''}${data.length} issue(s) · ${new Date().toLocaleString()}`,
    body: `
      <div class="table-wrap">
        <table>
          <thead><tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`,
    variant: 'table',
  });
}

function buildIssueReportBlock(issue, includeImages, getAssigneeName) {
  const id = issue.ncCode || issue.display_id || 'Issue';
  const topic = issue.topic || issue.identifiedIssue || '';
  const pathType = issue.pathType || issue.path_type || '';
  const compType = complianceTypeLabel(getIssueComplianceType(issue));
  const assignee = getAssigneeName ? getAssigneeName(issue.assignedTo) : issue.assignedTo || '—';
  const created = issue.formattedCreatedAt || issue.createdAt || '—';

  let pathSections = '';

  if (pathType === 'compliance_check') {
    pathSections = `
      <div class="report-section">
        <h4>Research result</h4>
        ${issue.researchFinding ? `<p class="report-body">${escapeHtml(issue.researchFinding)}</p>` : ''}
        <p><strong>Verdict:</strong> ${escapeHtml(verdictLabel(issue.verdict))}</p>
        <div class="report-subsection">
          <p><strong>Evidence document:</strong> ${escapeHtml(issue.evidenceDoc || '—')}</p>
          ${issue.evidenceLocation ? `<p><strong>Location:</strong> ${escapeHtml(issue.evidenceLocation)}</p>` : ''}
          ${issue.evidenceStatement ? `<p class="report-quote">${escapeHtml(issue.evidenceStatement)}</p>` : ''}
        </div>
      </div>`;
  } else if (pathType === 'conflict') {
    pathSections = `
      <div class="report-section">
        <h4>Conflicting documents</h4>
        <p><strong>Source A:</strong> ${escapeHtml(formatSourceBlock(issue.sourceA) || '—')}</p>
        <p><strong>Source B:</strong> ${escapeHtml(formatSourceBlock(issue.sourceB) || '—')}</p>
      </div>`;
  } else if (pathType === 'gap') {
    const absent = (issue.absentFrom || []).map((d) => d?.docName || d?.title || '').filter(Boolean);
    pathSections = `
      <div class="report-section">
        <h4>Research result</h4>
        ${issue.presentIn ? `<p><strong>Present in:</strong> ${escapeHtml(formatSourceBlock(issue.presentIn))}</p>` : ''}
        ${absent.length ? `<p><strong>Missing from:</strong> ${escapeHtml(absent.join('; '))}</p>` : ''}
      </div>`;
  }

  const resolutionAttachments = issue.resolutionAttachments || [];
  const solutions = issue.solutions || [];
  const images = issue.images || [];

  const resolutionSection =
    issue.overrideType ||
    issue.dismissalReason ||
    resolutionAttachments.length ||
    solutions.length
      ? `
      <div class="report-section">
        <h4>Resolution</h4>
        ${issue.overrideType ? `<p><strong>Override:</strong> ${escapeHtml(overrideTypeLabel(issue.overrideType))}</p>` : ''}
        ${issue.dismissalReason ? `<p><strong>Notes:</strong> ${escapeHtml(issue.dismissalReason)}</p>` : ''}
        ${
          resolutionAttachments.length
            ? `<p><strong>Attachments:</strong> ${escapeHtml(
                resolutionAttachments.map((a) => a.name || a.id).join('; ')
              )}</p>`
            : ''
        }
        ${
          solutions.length
            ? `<p><strong>Solutions:</strong> ${escapeHtml(
                solutions.map((s) => s.name || s.title || 'Solution').join('; ')
              )}</p>`
            : ''
        }
      </div>`
      : '';

  const imagesSection =
    includeImages && images.length
      ? `
      <div class="report-section">
        <h4>Images</h4>
        <ul>${images.map((src) => `<li>${escapeHtml(src)}</li>`).join('')}</ul>
      </div>`
      : '';

  return `
    <article class="issue-report">
      <header class="issue-report-header">
        <h3>${escapeHtml(id)}${topic ? ` — ${escapeHtml(topic)}` : ''}</h3>
        <p class="issue-meta">
          <span>${escapeHtml(statusLabel(issue.status))}</span>
          · <span>${escapeHtml(compType)}</span>
          · <span>${escapeHtml(pathTypeLabel(pathType) || 'Compliance check')}</span>
          · <span>Assignee: ${escapeHtml(assignee)}</span>
          · <span>${escapeHtml(created)}</span>
        </p>
      </header>
      <div class="report-section">
        <h4>Requirement</h4>
        ${issue.requirementArticle ? `<p><strong>Article:</strong> ${escapeHtml(issue.requirementArticle)}</p>` : ''}
        ${issue.expectedCompliance ? `<p class="report-body">${escapeHtml(issue.expectedCompliance)}</p>` : ''}
        ${formatRefs(issue.requirementRefs) ? `<p><strong>References:</strong> ${escapeHtml(formatRefs(issue.requirementRefs))}</p>` : ''}
      </div>
      ${pathSections}
      ${resolutionSection}
      ${imagesSection}
    </article>`;
}

function buildNarrativeReportHTML(data, timestamp, projectName, includeImages, getAssigneeName) {
  const body = data.map((issue) => buildIssueReportBlock(issue, includeImages, getAssigneeName)).join('');
  return wrapExportDocument({
    title: 'Compliance Report',
    subtitle: `${projectName ? `${escapeHtml(projectName)} · ` : ''}${data.length} issue(s) · ${new Date().toLocaleString()}`,
    body: `<div class="report-issues">${body}</div>`,
    variant: 'report',
  });
}

export function wrapExportDocument({ title, subtitle, body, variant }) {
  const tableStyles =
    variant === 'table'
      ? `
    .table-wrap { overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { background: #f8fafc; padding: 10px; text-align: left; border-bottom: 2px solid #e5e7eb; }
    td { padding: 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; word-wrap: break-word; }
    tr:nth-child(even) td { background: #f9fafb; }`
      : `
    .report-issues { display: flex; flex-direction: column; gap: 24px; }
    .issue-report { border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; page-break-inside: avoid; }
    .issue-report-header h3 { margin: 0 0 8px; font-size: 16px; color: #111827; }
    .issue-meta { margin: 0; font-size: 12px; color: #6b7280; }
    .report-section { margin-top: 16px; }
    .report-section h4 { margin: 0 0 8px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: #e97024; }
    .report-body, .report-quote { margin: 8px 0 0; font-size: 13px; line-height: 1.5; color: #374151; }
    .report-quote { padding: 10px 12px; background: #f8fafc; border-left: 3px solid #e97024; border-radius: 4px; }
    .report-subsection { margin-top: 8px; }`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { margin: 16px; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 16px; background: #f8fafc; color: #111827; }
    .container { max-width: 960px; margin: 0 auto; background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); overflow: hidden; }
    .header { padding: 20px 24px; background: #e97024; color: #fff; }
    .header h1 { margin: 0; font-size: 22px; }
    .header p { margin: 8px 0 0; font-size: 13px; opacity: 0.95; }
    .content { padding: 24px; }
    .footer { padding: 12px 24px 16px; text-align: center; color: #6b7280; font-size: 12px; border-top: 1px solid #e5e7eb; }
    ${tableStyles}
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>${escapeHtml(title)}</h1>
      <p>${subtitle}</p>
    </div>
    <div class="content">${body}</div>
    <div class="footer">Generated by CODiii Compliance Dashboard</div>
  </div>
</body>
</html>`;
}

export function exportToHTMLFile(html, filename) {
  downloadBlob(new Blob([html], { type: 'text/html;charset=utf-8;' }), filename);
}

function exportToPDF(html) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Pop-up blocked. Allow pop-ups to export PDF.');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

/**
 * @param {object} options
 * @param {'report'|'table'} options.mode
 * @param {'csv'|'html'|'pdf'|'excel'} options.format
 * @param {object[]} options.data
 * @param {string[]} [options.columns] - table mode only
 * @param {boolean} [options.includeImages]
 * @param {string} [options.projectName]
 * @param {(id: string) => string} [options.getAssigneeName]
 */
export function runComplianceExport({
  mode,
  format,
  data,
  columns = [],
  includeImages = false,
  projectName = '',
  getAssigneeName,
}) {
  const list = data || [];
  if (!list.length) {
    alert('No issues to export for the current selection.');
    return;
  }

  const timestamp = new Date().toISOString().split('T')[0];
  const safeProject = (projectName || 'project').replace(/[^\w-]+/g, '_').slice(0, 40);

  if (mode === 'table') {
    const cols =
      columns.length > 0 ? columns : EXPORT_TABLE_COLUMNS.map((c) => c.key);
    const base = `compliance_table_${safeProject}_${timestamp}`;
    if (format === 'csv') {
      exportTableToCSV(list, cols, `${base}.csv`, EXPORT_TABLE_COLUMNS, getAssigneeName);
      return;
    }
    if (format === 'excel') {
      exportTableToXlsx(list, cols, `${base}.xlsx`, EXPORT_TABLE_COLUMNS, getAssigneeName);
      return;
    }
    const html = buildTableHTML(
      list,
      cols,
      timestamp,
      projectName,
      EXPORT_TABLE_COLUMNS,
      getAssigneeName
    );
    if (format === 'pdf') {
      exportToPDF(html);
      return;
    }
    exportToHTMLFile(html, `compliance_table_${safeProject}_${timestamp}.html`);
    return;
  }

  // Narrative report
  const html = buildNarrativeReportHTML(
    list,
    timestamp,
    projectName,
    includeImages,
    getAssigneeName
  );
  if (format === 'pdf') {
    exportToPDF(html);
    return;
  }
  exportToHTMLFile(html, `compliance_report_${safeProject}_${timestamp}.html`);
}
